import * as Sentry from '@sentry/browser';
import useSWR, { mutate, SWRConfiguration } from 'swr';
import { fetchWithToken } from '@infra/http/fetchWithToken';
import { Request, Response } from './types';
export type { KeyedMutator } from 'swr';

export async function fetcher<Data = any>({
  url,
  method,
  headers,
  body,
  responseType = 'json',
  baseUrl = 'https://atlas-view.spotify.com',
}: Request): Promise<Response<Data>> {
  const response = await fetchWithToken(`${baseUrl}/${url}`, {
    method,
    headers,
    body,
  });

  return {
    ok: response.ok,
    status: response.status,
    data: await response[responseType]().catch(() => null),
  } as Response;
}

function toKey(request: Request) {
  // including the fetcher object in the key ensures the cache key is unique to useSWRFetch
  // @see https://swr.vercel.app/docs/arguments#passing-objects
  return [JSON.stringify(request), fetcher];
}

/**
 * Hook to load data from an api. Uses swr lib internally.
 * @param request object describing the request
 * @param swrConfig config passed to useSWR hook (docs: https://swr.vercel.app)
 */
export function useSWRFetch<Data = any>(
  request: Request | null,
  swrConfig?: SWRConfiguration<Response<Data>>,
  altFetcher?: Function | null,
) {
  const result = useSWR(
    request === null ? null : toKey(request),
    altFetcher
      ? () => altFetcher(request as Request)
      : () => fetcher<Data>(request as Request),
    swrConfig,
  );

  if (result.data?.status && result.data?.status >= 500) {
    Sentry.captureException('An internal server error occurred.');
  }

  return {
    ...result,
    ok: result.data?.ok,
    status: result.data?.status,
    data: result.data?.data,
    error:
      result.data?.status && result.data?.status !== 200
        ? 'An error occurred while fetching the data.'
        : null,
  };
}

/**
 * Update the data associated with a request
 * @param request object describing the request
 * @param data
 * @param shouldRevalidate
 * @see https://swr.vercel.app/docs/mutation
 */
export function mutateWebgate<Data = any>(
  request: Request,
  response?: Response<Data> | Promise<Response<Data>>,
  shouldRevalidate?: boolean,
) {
  return mutate(toKey(request), response, shouldRevalidate);
}
